import { Type, Loc, Text } from 'main.core';
import { BaseEvent } from 'main.core.events';

import { ItemNode } from './item-node';
import { SearchIndex } from '../search/search-index';
import { Entity } from '../entity/entity';
import { ItemBadge } from './item-badge';
import { TextNode } from '../common/text-node';
import { TypeUtils } from '../common/type-utils';

import { type Dialog } from '../dialog/dialog';
import { type ItemOptions, type ItemSelectOptions } from './item-options';
import { type ItemNodeOptions } from './item-node-options';
import { type ItemBadgeOptions } from './item-badge-options';
import { type TagItemOptions } from '../tag-selector/tag-item-options';
import { type TextNodeOptions } from '../common/text-node-options';
import { type CaptionOptions } from './caption-options';
import { type BadgesOptions } from './badges-options';
import { type AvatarOptions } from './avatar-options';

/**
 * @memberof BX.UI.EntitySelector
 * @package ui.entity-selector
 */
export class Item
{
	id: string | number;
	entityId: string;
	entityType: string;

	title: TextNode | null = null;
	subtitle: TextNode | null = null;
	supertitle: TextNode | null = null;
	caption: TextNode | null = null;
	captionOptions: { [key: string]: any } = {};
	avatar: string | null = null;
	avatarOptions: AvatarOptions | null = null;
	textColor: string | null = null;
	link: string | null = null;
	linkTitle: TextNode | null = null;
	tagOptions: Map<string, any>;
	badges: ItemBadge[] | null = null;
	badgesOptions: { [key: string]: any } = {};

	dialog!: Dialog;
	nodes: Set<ItemNode> = new Set();
	selected: boolean = false;
	searchable: boolean = true;
	saveable: boolean = true;
	deselectable: boolean = true;
	hidden: boolean = false;
	locked: boolean = false;
	searchIndex: SearchIndex | null = null;
	customData: Map<string, any>;

	sort: number | null = null;
	contextSort: number | null = null;
	globalSort: number | null = null;

	constructor(itemOptions: ItemOptions)
	{
		const options: ItemOptions = Type.isPlainObject(itemOptions) ? itemOptions : {} as ItemOptions;
		if (!Type.isStringFilled(options.id) && !Type.isNumber(options.id))
		{
			throw new Error('EntitySelector.Item: "id" parameter is required.');
		}

		if (!Type.isStringFilled(options.entityId))
		{
			throw new Error('EntitySelector.Item: "entityId" parameter is required.');
		}

		this.id = options.id;
		this.entityId = options.entityId.toLowerCase();
		this.entityType = Type.isStringFilled(options.entityType) ? options.entityType : 'default';

		this.locked = Type.isBoolean(options.locked) ? options.locked : false;
		this.selected = Type.isBoolean(options.selected) && !this.locked ? options.selected : false;

		this.customData = TypeUtils.createMapFromOptions(options.customData);
		this.tagOptions = TypeUtils.createMapFromOptions(options.tagOptions);

		this.setTitle(options.title);
		this.setSubtitle(options.subtitle);
		this.setSupertitle(options.supertitle);
		this.setCaption(options.caption);
		this.setCaptionOptions(options.captionOptions);
		this.setAvatar(options.avatar);
		this.setAvatarOptions(options.avatarOptions);
		this.setTextColor(options.textColor);
		this.setLink(options.link);
		this.setLinkTitle(options.linkTitle);
		this.setBadges(options.badges);
		this.setBadgesOptions(options.badgesOptions);

		this.setSearchable(options.searchable);
		this.setSaveable(options.saveable);
		this.setDeselectable(options.deselectable);
		this.setHidden(options.hidden);
		this.setContextSort(options.contextSort);
		this.setGlobalSort(options.globalSort);
		this.setSort(options.sort);
	}

	getId(): string | number
	{
		return this.id;
	}

	getEntityId(): string
	{
		return this.entityId;
	}

	getEntity(): Entity
	{
		let entity = this.getDialog().getEntity(this.getEntityId());
		if (entity === null)
		{
			entity = new Entity({ id: this.getEntityId() });
			this.getDialog().addEntity(entity);
		}

		return entity!;
	}

	getEntityType(): string
	{
		return this.entityType;
	}

	getTitle(): string
	{
		const titleNode = this.getTitleNode();

		return titleNode !== null && !titleNode!.isNullable() ? titleNode!.getText()! : '';
	}

	getTitleNode(): TextNode | null
	{
		return this.title;
	}

	setTitle(title: string | null | undefined | TextNodeOptions): void
	{
		if (Type.isStringFilled(title) || Type.isPlainObject(title) || title === null)
		{
			this.title = title === null ? null : new TextNode(title);

			this.resetSearchIndex();
			this.#renderNodes();
		}
	}

	getSubtitle(): string | null
	{
		const subtitleNode = this.getSubtitleNode();

		return subtitleNode === null ? null : subtitleNode!.getText();
	}

	getSubtitleNode(): TextNode | null
	{
		return this.subtitle === null ? this.getEntityTextNode('subtitle') : this.subtitle;
	}

	setSubtitle(subtitle: string | null | undefined | TextNodeOptions): void
	{
		if (Type.isString(subtitle) || Type.isPlainObject(subtitle) || subtitle === null)
		{
			this.subtitle = subtitle === null ? null : new TextNode(subtitle);

			this.resetSearchIndex();
			this.#renderNodes();
		}
	}

	getSupertitle(): string | null
	{
		const supertitleNode = this.getSupertitleNode();

		return supertitleNode === null ? null : supertitleNode!.getText();
	}

	getSupertitleNode(): TextNode | null
	{
		return this.supertitle === null ? this.getEntityTextNode('supertitle') : this.supertitle;
	}

	setSupertitle(supertitle: string | null | undefined | TextNodeOptions): void
	{
		if (Type.isString(supertitle) || Type.isPlainObject(supertitle) || supertitle === null)
		{
			this.supertitle = supertitle === null ? null : new TextNode(supertitle);

			this.resetSearchIndex();
			this.#renderNodes();
		}
	}

	getCaption(): string | null
	{
		const captionNode = this.getCaptionNode();

		return captionNode === null ? null : captionNode!.getText();
	}

	getCaptionNode(): TextNode | null
	{
		return this.caption === null ? this.getEntityTextNode('caption') : this.caption;
	}

	setCaption(caption: string | null | undefined | TextNodeOptions): void
	{
		if (Type.isString(caption) || Type.isPlainObject(caption) || caption === null)
		{
			this.caption = caption === null ? null : new TextNode(caption);

			this.resetSearchIndex();
			this.#renderNodes();
		}
	}

	getCaptionOption(option: string): string | boolean | number | null
	{
		if (!Type.isUndefined(this.captionOptions[option]))
		{
			return this.captionOptions[option];
		}

		const captionOptions = this.getEntityItemOption('captionOptions');
		if (Type.isPlainObject(captionOptions) && !Type.isUndefined(captionOptions[option]))
		{
			return captionOptions[option] as string | boolean | number | null;
		}

		return null;
	}

	setCaptionOption(option: string, value: string | boolean | number | null): void
	{
		if (Type.isStringFilled(option) && !Type.isUndefined(value))
		{
			this.captionOptions[option] = value;
			this.#renderNodes();
		}
	}

	setCaptionOptions(options: { [key: string]: any } | undefined): void
	{
		if (Type.isPlainObject(options))
		{
			Object.keys(options).forEach((option: string) => {
				this.setCaptionOption(option, options[option] as string | boolean | number | null);
			});
		}
	}

	getAvatar(): string | null
	{
		return this.avatar === null ? this.getEntityItemOption('avatar') : this.avatar;
	}

	setAvatar(avatar: string | null | undefined): void
	{
		if (Type.isString(avatar) || avatar === null)
		{
			this.avatar = avatar;
			this.#renderNodes();
		}
	}

	getAvatarOption(option: keyof AvatarOptions): string | boolean | number | null
	{
		if (this.avatarOptions !== null && !Type.isUndefined(this.avatarOptions[option]))
		{
			return this.avatarOptions[option] as string | boolean | number | null;
		}

		const avatarOptions = this.getEntityItemOption('avatarOptions');
		if (Type.isPlainObject(avatarOptions) && !Type.isUndefined(avatarOptions[option]))
		{
			return avatarOptions[option] as string | boolean | number | null;
		}

		return null;
	}

	setAvatarOption(option: keyof AvatarOptions, value: string | boolean | number | null): void
	{
		if (Type.isStringFilled(option) && !Type.isUndefined(value))
		{
			if (this.avatarOptions === null)
			{
				this.avatarOptions = {};
			}

			this.avatarOptions[option] = value as AvatarOptions[keyof AvatarOptions];
			this.#renderNodes();
		}
	}

	setAvatarOptions(options: AvatarOptions | undefined): void
	{
		if (Type.isPlainObject(options))
		{
			Object.keys(options).forEach((option: string) => {
				this.setAvatarOption(
					option as keyof AvatarOptions,
					(options as Record<string, string | boolean | number | null>)[option],
				);
			});
		}
	}

	getTextColor(): string | null
	{
		return this.textColor === null ? this.getEntityItemOption('textColor') : this.textColor;
	}

	setTextColor(textColor: string | null | undefined): void
	{
		if (Type.isString(textColor) || textColor === null)
		{
			this.textColor = textColor;
			this.#renderNodes();
		}
	}

	getLink(): string | null
	{
		const link = this.link === null ? this.getEntityItemOption('link') : this.link;

		return this.replaceMacros(link);
	}

	setLink(link: string | null | undefined): void
	{
		if (Type.isString(link) || link === null)
		{
			this.link = link;
			this.#renderNodes();
		}
	}

	getLinkTitle(): string | null
	{
		const linkTitleNode = this.getLinkTitleNode();

		return linkTitleNode === null ? (Loc.getMessage('UI_SELECTOR_ITEM_LINK_TITLE') || '') : linkTitleNode!.getText();
	}

	getLinkTitleNode(): TextNode | null
	{
		return this.linkTitle === null ? this.getEntityTextNode('linkTitle') : this.linkTitle;
	}

	setLinkTitle(linkTitle: string | null | TextNodeOptions | undefined): void
	{
		if (Type.isString(linkTitle) || Type.isPlainObject(linkTitle) || linkTitle === null)
		{
			this.linkTitle = linkTitle === null ? null : new TextNode(linkTitle);
			this.#renderNodes();
		}
	}

	getBadges(): ItemBadge[]
	{
		if (this.badges !== null)
		{
			return this.badges;
		}

		const badges = this.getEntity().getBadges(this);
		if (Type.isArray(badges))
		{
			this.badges = [];
			for (const badge of badges)
			{
				this.badges.push(new ItemBadge(badge));
			}
		}
		else
		{
			this.badges = [];
		}

		return this.badges;
	}

	setBadges(badges: ItemBadgeOptions[] | null | void): void
	{
		if (Type.isArray(badges))
		{
			this.badges = [];
			for (const badge of badges)
			{
				this.badges.push(new ItemBadge(badge));
			}

			this.#renderNodes();
		}
		else if (badges === null)
		{
			this.badges = null;
			this.#renderNodes();
		}
	}

	getBadgesOption(option: string): string | boolean | number | null
	{
		if (!Type.isUndefined(this.badgesOptions[option]))
		{
			return this.badgesOptions[option];
		}

		const badgesOptions = this.getEntityItemOption('badgesOptions');
		if (Type.isPlainObject(badgesOptions) && !Type.isUndefined(badgesOptions[option]))
		{
			return badgesOptions[option] as string | boolean | number | null;
		}

		return null;
	}

	setBadgesOption(option: string, value: string | boolean | number | null): void
	{
		if (Type.isStringFilled(option) && !Type.isUndefined(value))
		{
			this.badgesOptions[option] = value;
			this.#renderNodes();
		}
	}

	setBadgesOptions(options: { [key: string]: any } | undefined): void
	{
		if (Type.isPlainObject(options))
		{
			Object.keys(options).forEach((option: string) => {
				this.setBadgesOption(option, options[option] as string | boolean | number | null);
			});
		}
	}

	/**
	 * @internal
	 */
	setDialog(dialog: Dialog): void
	{
		this.dialog = dialog;
	}

	getDialog(): Dialog
	{
		return this.dialog;
	}

	createNode(nodeOptions: ItemNodeOptions): ItemNode
	{
		const itemNode = new ItemNode(this, nodeOptions);
		this.nodes.add(itemNode);

		return itemNode;
	}

	removeNode(node: ItemNode): void
	{
		this.nodes.delete(node);
	}

	getNodes(): Set<ItemNode>
	{
		return this.nodes;
	}

	select(selectOptions: ItemSelectOptions = {}): void
	{
		if (this.selected)
		{
			return;
		}

		const options: ItemSelectOptions = (
			Type.isBoolean(selectOptions) // Compatibility signature select(preselectedMode: boolean = false)
				? { emitEvents: !selectOptions, animate: !selectOptions }
				: selectOptions
		);

		const { emitEvents, animate, node } = {
			emitEvents: true,
			animate: true,
			node: null,
			...options,
		};

		const dialog = this.getDialog();
		if (dialog && emitEvents)
		{
			const event = new BaseEvent({ data: { item: this, node } });
			dialog.emit('Item:onBeforeSelect', event);
			if (event.isDefaultPrevented())
			{
				return;
			}
		}

		if (this.isLocked())
		{
			return;
		}

		this.selected = true;

		if (dialog)
		{
			dialog.handleItemSelect(this, animate);
		}

		if (this.isRendered())
		{
			for (const itemNode of this.getNodes())
			{
				itemNode.select();
			}
		}

		if (dialog && emitEvents)
		{
			dialog.emit('Item:onSelect', { item: this, node });
			dialog.saveRecentItem(this);
		}
	}

	deselect(deselectOptions: ItemSelectOptions = {}): void
	{
		if (!this.selected)
		{
			return;
		}

		const options: ItemSelectOptions = (
			Type.isBoolean(deselectOptions) // Compatibility signature select(preselectedMode: boolean = false)
				? { emitEvents: !deselectOptions, animate: !deselectOptions }
				: deselectOptions
		);

		const { emitEvents, animate, node } = {
			emitEvents: true,
			animate: true,
			node: null,
			...options,
		};

		const dialog = this.getDialog();
		if (dialog && emitEvents)
		{
			const event = new BaseEvent({ data: { item: this, node } });
			dialog.emit('Item:onBeforeDeselect', event);
			if (event.isDefaultPrevented())
			{
				return;
			}
		}

		this.selected = false;

		if (this.isRendered())
		{
			this.getNodes().forEach((itemNode: ItemNode) => {
				itemNode.deselect();
			});
		}

		if (dialog)
		{
			dialog.handleItemDeselect(this, animate);
		}

		if (dialog && emitEvents)
		{
			dialog.emit('Item:onDeselect', { item: this, node });
		}
	}

	isSelected(): boolean
	{
		return this.selected;
	}

	setSearchable(flag: boolean | undefined): void
	{
		if (Type.isBoolean(flag))
		{
			this.searchable = flag;
		}
	}

	isSearchable(): boolean
	{
		return this.searchable;
	}

	setSaveable(flag: boolean | undefined): void
	{
		if (Type.isBoolean(flag))
		{
			this.saveable = flag;
		}
	}

	isSaveable(): boolean
	{
		return this.saveable;
	}

	setDeselectable(flag: boolean | undefined): void
	{
		if (Type.isBoolean(flag))
		{
			this.deselectable = flag;

			const tagSelector = this.getDialog()?.getTagSelector() || null;
			if (tagSelector !== null)
			{
				const tag = tagSelector.getTag({
					id: this.getId(),
					entityId: this.getEntityId(),
				});

				if (tag)
				{
					tag.setDeselectable(flag);
				}
			}
		}
	}

	isDeselectable(): boolean
	{
		return this.deselectable;
	}

	setHidden(flag: boolean | undefined): void
	{
		if (Type.isBoolean(flag))
		{
			this.hidden = flag;

			if (this.isRendered())
			{
				this.getNodes().forEach((node: ItemNode) => {
					node.setHidden(flag);
				});
			}
		}
	}

	isHidden(): boolean
	{
		return this.hidden;
	}

	lock(): void
	{
		if (this.locked)
		{
			return;
		}

		const dialog = this.getDialog();
		if (dialog)
		{
			const event = new BaseEvent({ data: { item: this } });
			dialog.emit('Item:onBeforeLock', event);
			if (event.isDefaultPrevented())
			{
				return;
			}
		}

		if (this.isSelected())
		{
			return;
		}

		this.locked = true;

		if (this.isRendered())
		{
			for (const node of this.getNodes())
			{
				node.lock();
			}
		}

		if (dialog)
		{
			dialog.emit('Item:onLock', { item: this });
		}
	}

	unlock(): void
	{
		if (!this.locked)
		{
			return;
		}

		const dialog = this.getDialog();
		if (dialog)
		{
			const event = new BaseEvent({ data: { item: this } });
			dialog.emit('Item:onBeforeUnlock', event);
			if (event.isDefaultPrevented())
			{
				return;
			}
		}

		this.locked = false;

		if (this.isRendered())
		{
			for (const node of this.getNodes())
			{
				node.unlock();
			}
		}

		if (dialog)
		{
			dialog.emit('Item:onUnlock', { item: this });
		}
	}

	isLocked(): boolean
	{
		return this.locked;
	}

	setContextSort(sort: number | null | undefined): void
	{
		if (Type.isNumber(sort) || sort === null)
		{
			this.contextSort = sort;
		}
	}

	getContextSort(): number | null
	{
		return this.contextSort;
	}

	setGlobalSort(sort: number | null | undefined): void
	{
		if (Type.isNumber(sort) || sort === null)
		{
			this.globalSort = sort;
		}
	}

	getGlobalSort(): number | null
	{
		return this.globalSort;
	}

	setSort(sort: number | null | undefined): void
	{
		if (Type.isNumber(sort) || sort === null)
		{
			this.sort = sort;
		}
	}

	getSort(): number | null
	{
		return this.sort;
	}

	getSearchIndex(): SearchIndex
	{
		if (this.searchIndex === null)
		{
			this.searchIndex = SearchIndex.create(this);
		}

		return this.searchIndex;
	}

	resetSearchIndex(): void
	{
		this.searchIndex = null;
	}

	getCustomData(): Map<string, any>
	{
		return this.customData;
	}

	setCustomData(property: string | null | undefined | { [key: string]: any }, value?: any): void
	{
		if (Type.isNull(property))
		{
			this.customData = new Map();
			this.#renderNodes();
		}
		else if (Type.isPlainObject(property))
		{
			Object.entries(property).forEach((item) => {
				const [currentKey, currentValue] = item;
				this.customData.set(currentKey, currentValue);
			});

			this.#renderNodes();
		}
		else if (Type.isString(property))
		{
			if (Type.isNull(value))
			{
				this.customData.delete(property);
				this.#renderNodes();
			}
			else if (!Type.isUndefined(value))
			{
				this.customData.set(property, value);
				this.#renderNodes();
			}
		}
	}

	isRendered(): boolean
	{
		return this.getDialog() && this.getDialog().isRendered();
	}

	#renderNodes(): void
	{
		if (this.isRendered())
		{
			for (const node of this.getNodes())
			{
				node.render();
			}
		}
	}

	getEntityItemOption(option: string): any
	{
		return this.getEntity().getItemOption(option, this.getEntityType());
	}

	getEntityTagOption(option: string): any
	{
		return this.getEntity().getTagOption(option, this.getEntityType());
	}

	getEntityTextNode(option: string): TextNode | null
	{
		return this.getEntity().getOptionTextNode(option, this.getEntityType());
	}

	getTagOptions(): Map<string, any>
	{
		return this.tagOptions;
	}

	getTagOption(option: string): any
	{
		const value = this.getTagOptions().get(option);

		if (!Type.isUndefined(value))
		{
			return value;
		}

		return null;
	}

	getTagGlobalOption(option: string, useItemOptions: boolean = false): any
	{
		if (!Type.isStringFilled(option))
		{
			return null;
		}

		let value = this.getTagOption(option);

		if (value === null && useItemOptions === true && (this as unknown as Record<string, any>)[option] !== null)
		{
			value = (this as unknown as Record<string, any>)[option];
		}

		if (value === null && this.getDialog().getTagSelector())
		{
			const fn = `getTag${Text.toPascalCase(option)}`;
			if (Type.isFunction((this.getDialog().getTagSelector()! as unknown as Record<string, any>)[fn]))
			{
				value = (this.getDialog().getTagSelector()! as unknown as Record<string, any>)[fn]();
			}
		}

		if (value === null)
		{
			value = this.getEntityTagOption(option);
		}

		if (value === null && useItemOptions === true)
		{
			value = this.getEntityItemOption(option);
		}

		return value;
	}

	getTagBgColor(): string | null
	{
		return this.getTagGlobalOption('bgColor');
	}

	getTagTextColor(): string | null
	{
		return this.getTagGlobalOption('textColor');
	}

	getTagMaxWidth(): number | null
	{
		return this.getTagGlobalOption('maxWidth');
	}

	getTagFontWeight(): string | null
	{
		return this.getTagGlobalOption('fontWeight');
	}

	getTagAvatar(): string | null
	{
		return this.getTagGlobalOption('avatar', true);
	}

	getTagAvatarOptions(): AvatarOptions | null
	{
		return this.getTagGlobalOption('avatarOptions', true);
	}

	getTagLink(): string | null
	{
		return this.replaceMacros(this.getTagGlobalOption('link', true));
	}

	/**
	 * @internal
	 */
	replaceMacros(str: string): string
	{
		if (!Type.isStringFilled(str))
		{
			return str;
		}

		const id = String(this.getId());

		return str.replace(/#id#/i, id).replace(/#element_id#/i, id);
	}

	/**
	 * @internal
	 */
	createTag(): Record<string, any>
	{
		const titleNode = this.getTitleNode();

		return {
			id: this.getId(),
			entityId: this.getEntityId(),
			entityType: this.getEntityType(),
			title: this.getTagOption('title') || (titleNode && titleNode.toJSON()) || '',
			deselectable: this.isDeselectable(),
			avatar: this.getTagAvatar(),
			avatarOptions: this.getTagAvatarOptions(),
			link: this.getTagLink(),
			maxWidth: this.getTagMaxWidth(),
			textColor: this.getTagTextColor(),
			bgColor: this.getTagBgColor(),
			fontWeight: this.getTagFontWeight(),
			onclick: this.getTagOption('onclick'),
		};
	}

	getAjaxJson(): { [key: string]: any }
	{
		return this.toJSON();
	}

	toJSON(): { [key: string]: any }
	{
		return {
			id: this.getId(),
			entityId: this.getEntityId(),
			entityType: this.getEntityType(),
			selected: this.isSelected(),
			deselectable: this.isDeselectable(),
			searchable: this.isSearchable(),
			saveable: this.isSaveable(),
			hidden: this.isHidden(),
			locked: this.isLocked(),
			title: this.getTitleNode(),
			link: this.getLink(),
			linkTitle: this.getLinkTitleNode(),
			subtitle: this.getSubtitleNode(),
			supertitle: this.getSupertitleNode(),
			caption: this.getCaptionNode(),
			avatar: this.getAvatar(),
			textColor: this.getTextColor(),
			sort: this.getSort(),
			contextSort: this.getContextSort(),
			globalSort: this.getGlobalSort(),
			customData: TypeUtils.convertMapToObject(this.getCustomData()),
			tagOptions: TypeUtils.convertMapToObject(this.getTagOptions()),
			badges: this.getBadges(),
		};
	}
}
